// Core
import { NgModule } from "@angular/core";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DecimalPipe, TitleCasePipe } from "@angular/common";

// Interceptors
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from "@angular/common/http";
import { ErrorInterceptor } from "./helpers/error.interceptor";

// Ng Bootstrap
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";

// Forms
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";

// Modules
import { FontAwesome } from "./modules/font-awesome/font-awesome.module";
import { SharedModule } from "./modules/shared/shared.module";
import { TourMatMenuModule } from "ngx-ui-tour-md-menu";
import { MatTooltipModule } from "@angular/material/tooltip";

// Services
import { ResizeService } from "./services/resize.service";
import { CookieService } from "ngx-cookie-service";
import { NavigationService } from "./components/navigation/navigation.service";
import { TitleService } from "./services/title.service";

// Routing
import { AppRouting } from "./app.routing";

// Translate
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from "@ngx-translate/http-loader";

import { environment } from "../environments/environment";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from "@angular/material/slide-toggle";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha";
import { Constants } from "src/app/constants/constants";
import { QueryClient, provideTanStackQuery, withDevtools } from "@tanstack/angular-query-experimental";

const tanStackClient = new QueryClient({ defaultOptions: { queries: { gcTime: Constants.DAY } } });

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule.withConfig({ callSetDisabledState: "whenDisabledForLegacyCode" }),
        NgSelectModule,
        HttpClientModule,
        HttpClientXsrfModule.withOptions({ cookieName: "ZM-XSRF-TOKEN" }),
        FontAwesome,
        SharedModule,
        NgbModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateHttpLoader
            }
        }),
        TourMatMenuModule,
        MatTooltipModule,
        RecaptchaV3Module,
        AppRouting
    ],
    providers: [
        Title,
        TitleService,
        ResizeService,
        CookieService,
        NavigationService,
        DecimalPipe,
        TitleCasePipe,
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: TRANSLATE_HTTP_LOADER_CONFIG, useValue: { prefix: "assets/i18n/" } },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: "dynamic" } },
        // Material defaults slide toggles to the accent palette, which this theme maps to grey;
        // primary is the palette the --mat-slide-toggle-* tokens in styles.scss are written for.
        { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { color: "primary" } },
        // CDK 21 renders overlays via the native Popover API (browser top layer),
        // where z-index no longer controls stacking between overlays (e.g. a tour
        // popover vs. a menu it opens). Revert to classic in-container z-index
        // stacking so our overlay/tour z-index rules apply. See styles.scss.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.production ? Constants.RECAPTCHA_PROD_KEY : Constants.RECAPTCHA_DEV_KEY
        },
        process.env.NODE_ENV === "development"
            ? provideTanStackQuery(tanStackClient, withDevtools())
            : provideTanStackQuery(tanStackClient)
    ]
})
export class AppModule {
    // Diagnostic only: inspect router configuration
    constructor(/*router: Router*/) {
        // Use a custom replacer to display function names in the route configs
        // const replacer = (key, value) => (typeof value === "function" ? value.name : value);
        // console.log("Routes: ", JSON.stringify(router.config, replacer, 2));
    }
}
